import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ShoppingCart, PackageX, Hand, Truck, Wallet, ReceiptText, Check } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { nok, checkoutUrl, stockMeta } from "../../lib/shop";
import { addToCart, useCart } from "../../lib/cart";
import { useLang } from "../../context/LanguageContext";

/**
 * Kompakt kjøpskort for éi vare: pris/lager, tal, «Legg i handlekorg» og «Kjøp nå» (legg i korg + gå til kassa).
 * Korga er éi per arrangør – legg du til frå ein annan arrangør, spør vi om byte. Sjølve kassa (levering/betaling/kjøpar) ligg på /butikk/:org/kasse.
 */
export const BuyCard = ({ product, shop, className = "" }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { cart } = useCart();
  const org = shop.organizer || {};
  const soldOut = product.stock_status === "sold_out";
  const maxQty = Math.max(1, product.max_per_order || 1);
  const inCart = cart.items.find((i) => i.product_id === product.id);
  const [qty, setQty] = useState(1);
  const [conflict, setConflict] = useState(null); // { mode: "add" | "buy" }
  const [added, setAdded] = useState(false);
  const stock = stockMeta(product);

  const changeQty = (next) => setQty(Math.min(maxQty, Math.max(1, Number.isFinite(next) ? next : 1)));

  const doAdd = (mode, replace = false) => {
    const res = addToCart(product, org, qty, { replace });
    if (!res.ok) {
      if (res.reason === "other_organizer") {
        setConflict({ mode, otherName: res.cart.organizer_name });
        return false;
      }
      toast.error(t("Handlekurven er full (maks {n} ulike varer)", { n: 10 }));
      return false;
    }
    if (mode === "buy") {
      navigate(checkoutUrl(product.organizer_id));
      return true;
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
    toast.success(t("{name} er lagt i handlekurven", { name: product.name }), {
      action: { label: t("Til kassen"), onClick: () => navigate(checkoutUrl(product.organizer_id)) },
    });
    return true;
  };

  return (
    <Card className={`border-border shadow-lg ${className}`} data-testid="buy-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-muted-foreground">{t("Pris")}</p>
            <p className="text-2xl font-heading font-semibold tabular-nums" data-testid="buy-card-price">{product.price === 0 ? t("Gratis") : nok(product.price)}</p>
          </div>
          {stock ? (
            <Badge className={stock.className} data-testid="buy-card-stock-badge">{stock.label}</Badge>
          ) : (
            <Badge variant="outline" className="text-[hsl(var(--success))] border-[hsl(var(--success))]/40" data-testid="buy-card-stock-badge">{t("På lager")}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {soldOut ? (
          <div className="rounded-xl bg-secondary p-6 text-center space-y-2" data-testid="buy-card-sold-out">
            <PackageX className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="font-medium">{t("Varen er utsolgt")}</p>
          </div>
        ) : !shop.can_sell ? (
          <div className="rounded-xl bg-secondary p-6 text-center text-sm text-muted-foreground" data-testid="buy-card-closed">{t("Butikken tar ikke imot bestillinger akkurat nå.")}</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="buy-qty">{t("Antall")}</Label>
              <div className="inline-flex items-center rounded-lg border border-border">
                <Button type="button" variant="ghost" size="icon" className="h-11 w-11 rounded-r-none" onClick={() => changeQty(qty - 1)} disabled={qty <= 1} aria-label={t("Færre")} data-testid="buy-quantity-decrease-button"><Minus className="w-4 h-4" /></Button>
                <Input id="buy-qty" type="number" min={1} max={maxQty} inputMode="numeric" value={qty} onChange={(e) => changeQty(parseInt(e.target.value, 10))} className="h-11 w-16 text-center border-0 border-x border-border rounded-none tabular-nums focus-visible:ring-0" data-testid="buy-quantity-input" />
                <Button type="button" variant="ghost" size="icon" className="h-11 w-11 rounded-l-none" onClick={() => changeQty(qty + 1)} disabled={qty >= maxQty} aria-label={t("Flere")} data-testid="buy-quantity-increase-button"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            <div className="grid gap-2">
              <Button type="button" className="h-12 text-base" onClick={() => doAdd("buy")} data-testid="buy-now-button">
                <ShoppingBag className="w-5 h-5 mr-2" /> {t("Kjøp nå – {amount}", { amount: nok(product.price * qty) })}
              </Button>
              <Button type="button" variant="outline" className="h-11" onClick={() => doAdd("add")} data-testid="add-to-cart-button">
                {added ? <Check className="w-4 h-4 mr-2 text-[hsl(var(--success))]" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                {added ? t("Lagt i handlekurven") : t("Legg i handlekurv")}
              </Button>
              {inCart && (
                <p className="text-xs text-muted-foreground text-center" data-testid="buy-card-in-cart-note">
                  {t("{n} stk. ligger allerede i handlekurven", { n: inCart.quantity })} ·{" "}
                  <button type="button" className="underline underline-offset-4 hover:text-foreground" onClick={() => navigate(checkoutUrl(product.organizer_id))} data-testid="buy-card-go-to-cart">{t("Til kassen")}</button>
                </p>
              )}
            </div>

            <div className="grid gap-1.5 text-xs text-muted-foreground" data-testid="buy-card-facts">
              {shop.pickup_enabled && <span className="inline-flex items-center gap-1.5"><Hand className="w-3.5 h-3.5 text-brand" /> {t("Henting hos arrangøren")}{shop.pickup_place ? ` · ${shop.pickup_place}` : ""}</span>}
              {shop.shipping_enabled && <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-brand" /> {t("Sending i posten")} · {Number(shop.shipping_price || 0) === 0 ? t("Gratis frakt") : t("Frakt {price}", { price: nok(shop.shipping_price) })}</span>}
              <span className="inline-flex items-center gap-1.5">
                {shop.vipps_available && <><Wallet className="w-3.5 h-3.5 text-brand" /> {t("Vipps")}</>}
                {shop.vipps_available && shop.invoice_available && <span>·</span>}
                {shop.invoice_available && <><ReceiptText className="w-3.5 h-3.5 text-brand" /> {t("Faktura")}</>}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed" data-testid="buy-terms-note">
              {t("Selger er {org} (org.nr. {orgnr}). Betalingen går direkte til selgeren – Right in formidler bare bestillingen.", { org: org.org_name || "", orgnr: org.org_number || "–" })}
            </p>
          </>
        )}
      </CardContent>

      <AlertDialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <AlertDialogContent data-testid="cart-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Bytte butikk?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Handlekurven din har varer fra {other}. Du kan bare handle hos én arrangør om gangen – vil du tømme kurven og legge til {name} i stedet?", { other: conflict?.otherName || t("en annen arrangør"), name: product.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cart-switch-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const mode = conflict?.mode; setConflict(null); doAdd(mode, true); }} data-testid="cart-switch-confirm">{t("Tøm og legg til")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
